import { useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, MapPin, FileText, ArrowLeft, Check, Loader2, Trophy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TeamScheduleMatch = () => {
  const { currentTeam } = useOutletContext();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const teamId = currentTeam?.team_id;

  const [formData, setFormData] = useState({
    opponent: '',
    match_date: '',
    location: '',
    notes: '',
    duration_minutes: 90
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.opponent || !formData.match_date) {
      toast.error('Please fill opponent and date');
      return;
    }

    setSubmitting(true);
    const token = localStorage.getItem('team_token');
    try {
      const res = await fetch(`${API}/clubs/team/${teamId}/schedule-match`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          opponent: formData.opponent,
          match_date: new Date(formData.match_date).toISOString(),
          location: formData.location || null,
          notes: formData.notes || null,
          duration_minutes: formData.duration_minutes
        })
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Match scheduled and added to calendar!');
      navigate(`/team/${teamId}/sessions`);
    } catch (e) {
      toast.error('Failed to schedule match');
    }
    setSubmitting(false);
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="schedule-match">
      <div className="mb-6">
        <h1 className="font-heading text-2xl uppercase">
          Schedule <span className="text-primary">Next Match</span>
        </h1>
        <p className="text-muted-foreground text-sm mt-1">{currentTeam?.team_name} — Match will be added to team calendar</p>
      </div>

      <motion.form
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        onSubmit={handleSubmit}
        className="bg-card border border-border/40 rounded-sm p-6 space-y-5"
      >
        <div>
          <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2">
            <Trophy size={13} className="text-primary" /> Opponent
          </label>
          <input
            value={formData.opponent}
            onChange={e => setFormData(p => ({ ...p, opponent: e.target.value }))}
            placeholder="Opponent team name..."
            className="w-full bg-zinc-900 border border-border/40 rounded-sm px-3 py-2.5 text-sm outline-none focus:border-primary/50"
            data-testid="schedule-opponent"
            required
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2">
            <Calendar size={13} className="text-primary" /> Date & Time
          </label>
          <input
            type="datetime-local"
            value={formData.match_date}
            onChange={e => setFormData(p => ({ ...p, match_date: e.target.value }))}
            className="w-full bg-zinc-900 border border-border/40 rounded-sm px-3 py-2.5 text-sm outline-none focus:border-primary/50 [color-scheme:dark]"
            data-testid="schedule-date"
            required
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2">
            <MapPin size={13} className="text-primary" /> Location
          </label>
          <input
            value={formData.location}
            onChange={e => setFormData(p => ({ ...p, location: e.target.value }))}
            placeholder="Match location (optional)..."
            className="w-full bg-zinc-900 border border-border/40 rounded-sm px-3 py-2.5 text-sm outline-none focus:border-primary/50"
            data-testid="schedule-location"
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2">
            <FileText size={13} className="text-primary" /> Notes
          </label>
          <textarea
            value={formData.notes}
            onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
            placeholder="Additional notes (optional)..."
            rows={3}
            className="w-full bg-zinc-900 border border-border/40 rounded-sm px-3 py-2.5 text-sm outline-none focus:border-primary/50 resize-none"
            data-testid="schedule-notes"
          />
        </div>

        <div className="flex items-center justify-between pt-2">
          <Button type="button" variant="outline" onClick={() => navigate(`/team/${teamId}`)}
            className="border-border/40"
          >
            <ArrowLeft size={16} className="mr-2" /> Cancel
          </Button>

          <Button type="submit" disabled={submitting}
            className="bg-primary text-black hover:bg-primary/90"
            data-testid="schedule-submit-btn"
          >
            {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : <Check size={16} className="mr-2" />}
            Schedule Match
          </Button>
        </div>
      </motion.form>
    </div>
  );
};

export default TeamScheduleMatch;
